import UsersTableHeader from "components/Headers/UsersTableHeader.js";
import {
  Card,
  CardBody,
  CardHeader,
  Form,
  FormGroup,
  Input,
  InputGroup,
  CardFooter,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Table,
  Container,
  Row,
  Col,
  Button,
  Modal,
  Pagination,
  PaginationItem,
  PaginationLink,
  Label
} from "reactstrap";
import { useParams, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import ResultsPerPage from "components/ResultsPerPage.js";
import Skeleton from "react-loading-skeleton";
import axios from "axios";
import { ReactSession } from "react-client-session";

/**
 * Teachers table with the same clean query‑string handling we applied to the
 * Classrooms table. Filters are optional; only non‑empty values are sent.
 */
export default function SchoolTeachersTable() {
  const { id } = useParams();
  const BASE_URL = process.env.REACT_APP_BASE_URL;

  /* ------------------------------------------------------------------ *
   * State
   * ------------------------------------------------------------------ */
  const [teachers, setTeachers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // filters
  const [schoolYearId, setSchoolYearId] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [teacherID, setTeacherID] = useState(""); // hidden text filter, UI optional
  const [address, setAddress] = useState("");     //      "
  const [weeklyOrders, setWeeklyOrders] = useState(""); // "

  // pagination
  const [page, setPage] = useState(1);
  const [pageLimit, setPageLimit] = useState(25);
  const [numberPages, setNumberPages] = useState(1);

  // school‑year dropdown data
  const [schoolYears, setSchoolYears] = useState([]);

  // react‑hook‑form helpers for the edit modal
  const { register, handleSubmit, reset, control } = useForm();

  // edit modal
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState({});

  /* ------------------------------------------------------------------ *
   * Helpers
   * ------------------------------------------------------------------ */
  const token = ReactSession.get("token");
  const axiosConfig = {
    headers: { Authorization: `Bearer ${token}` },
  };

  const fetchTeachers = async () => {
    setIsLoading(true);

    // Build params object – Axios will serialize this
    const params = {
      page,
      limit: pageLimit,
      ...(schoolYearId && { school_year_id: schoolYearId }),
      ...(firstName     && { first_name:      firstName }),
      ...(lastName      && { last_name:       lastName }),
      ...(teacherID     && { id:              teacherID }),
      ...(address       && { address }),
      ...(weeklyOrders  && { has_weekly_orders: weeklyOrders }),
    };

    try {
      const { data } = await axios.get(
        `${BASE_URL}/schools/${id}/teachers/`,
        { params, ...axiosConfig }
      );

      setTeachers(data.data);
      setNumberPages(data.meta?.last_page || 1);
    } catch (err) {
      console.error("Could not fetch teachers", err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSchoolYears = async () => {
    try {
      const { data, status } = await axios.get(
        `${BASE_URL}/school_years/`,
        axiosConfig
      );
      if (status === 200) setSchoolYears(data);
    } catch (err) {
      console.error("Could not fetch school years", err);
    }
  };

  /* ------------------------------------------------------------------ *
   * Side‑effects
   * ------------------------------------------------------------------ */
  // on mount – pull dropdown data
  useEffect(() => {
    fetchSchoolYears();
    // try to prime school‑year from localStorage just once
    const stored = localStorage.getItem("selectedSchoolYear");
    if (stored) setSchoolYearId(JSON.parse(stored).id);
  }, []);

  // whenever a filter / page changes, reload table
  useEffect(() => {
    fetchTeachers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageLimit, schoolYearId, firstName, lastName, teacherID, address, weeklyOrders]);

  /* ------------------------------------------------------------------ *
   * Edit modal helpers
   * ------------------------------------------------------------------ */
  const handleEditSubmit = async (form) => {
    const url = `${BASE_URL}/teachers/${modalData.id}/edit`;
    const payload = {
      firstName:    form.newFirstName,
      lastName:     form.newLastName,
      phoneNumber:  form.newPhoneNumber,
      email:        form.newEmail,
    };

    try {
      await axios.put(url, payload, { headers: { "Content-Type": "application/json" } });
      setModalOpen(false);
      fetchTeachers();
    } catch (err) {
      console.error("Edit failed", err);
    }
  };

  /* ------------------------------------------------------------------ *
   * Render
   * ------------------------------------------------------------------ */
  return (
    <>
      <UsersTableHeader />
      <Container fluid className="mt--7">
        <Card>
          <CardHeader>
            <Row className="align-items-center justify-content-between">
              <h1 className="ml-4 mr-4">School Teachers</h1>
            </Row>
          </CardHeader>

          {/* ----------------------------------------------------------------- */}
          {/* Filters */}
          {/* ----------------------------------------------------------------- */}
          <Form className="pl-lg-4 pt-2">
            <Row>
              <FormGroup className="col" row>
                <Col>
                  <Label className="form-control-label" htmlFor="school-year-select">School Year</Label>
                  <InputGroup>
                    <Input
                      id="school-year-select"
                      type="select"
                      value={schoolYearId}
                      onChange={(e) => {
                        setSchoolYearId(e.target.value);
                        setPage(1);
                      }}
                    >
                      <option value="">Select a school year</option>
                      {schoolYears.map((y) => (
                        <option key={y.id} value={y.id}>{y.name}</option>
                      ))}
                    </Input>
                  </InputGroup>
                </Col>
                <Col>
                  <Label className="form-control-label" htmlFor="first-name-filter">First Name</Label>
                  <InputGroup>
                    <Input
                      id="first-name-filter"
                      type="text"
                      placeholder="Enter first name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                    />
                  </InputGroup>
                </Col>
                <Col>
                  <Label className="form-control-label" htmlFor="last-name-filter">Last Name</Label>
                  <InputGroup>
                    <Input
                      id="last-name-filter"
                      type="text"
                      placeholder="Enter last name"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                    />
                  </InputGroup>
                </Col>
              </FormGroup>
            </Row>
            <Row>
              <Col>
                <ResultsPerPage
                  pageLimit={pageLimit}
                  setPageLimit={setPageLimit}
                  page={page}
                  setPage={setPage}
                />
              </Col>
            </Row>
          </Form>

          {/* ----------------------------------------------------------------- */}
          {/* Table */}
          {/* ----------------------------------------------------------------- */}
          <CardBody>
            {isLoading ? (
              <Skeleton count={5} />
            ) : (
              <Table>
                <thead className="thead-light">
                  <tr className="text-center">
                    <th>ID</th>
                    <th>First Name</th>
                    <th>Last Name</th>
                    <th>Campus</th>
                    <th>Classroom</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {teachers.map((t) => (
                    <tr key={t.id} className="text-center">
                      <td>{t.id}</td>
                      <td>{t.first_name}</td>
                      <td>{t.last_name}</td>
                      <td>{t.classroom?.campus?.name ?? "—"}</td>
                      <td>{t.classroom?.class_number ?? "—"}</td>
                      <td className="text-right">
                        <UncontrolledDropdown>
                          <DropdownToggle
                            className="btn-icon-only text-light"
                            role="button"
                            size="sm"
                            color="success"
                            onClick={(e) => e.preventDefault()}
                          >
                            <i className="fas fa-ellipsis-v" />
                          </DropdownToggle>
                          <DropdownMenu right className="dropdown-menu-arrow">
                            <DropdownItem onClick={() => { setModalData(t); setModalOpen(true); }}>
                              Edit
                            </DropdownItem>
                            <DropdownItem
                              onClick={() => {
                                axios.put(`${BASE_URL}/teachers/${t.id}/edit`, { isDeleted: true }, { headers: { "Content-Type": "application/json" } })
                                  .finally(fetchTeachers);
                              }}
                            >
                              Delete
                            </DropdownItem>
                          </DropdownMenu>
                        </UncontrolledDropdown>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </CardBody>

          {/* ----------------------------------------------------------------- */}
          {/* Pagination */}
          {/* ----------------------------------------------------------------- */}
          <CardFooter className="py-4">
            <nav aria-label="Teachers pagination">
              <Pagination className="pagination justify-content-end mb-0">
                {Array.from({ length: numberPages }, (_, i) => i + 1).map((p) => (
                  <PaginationItem key={p} active={page === p}>
                    <PaginationLink onClick={() => setPage(p)}>{p}</PaginationLink>
                  </PaginationItem>
                ))}
              </Pagination>
            </nav>
          </CardFooter>
        </Card>
      </Container>

      {/* ------------------------------------------------------------------- */}
      {/* Edit modal */}
      {/* ------------------------------------------------------------------- */}
      <Modal size="md" isOpen={modalOpen} toggle={() => setModalOpen((o) => !o)} className="modal-dialog-centered">
        <div className="modal-body p-0">
          <Card className="bg-secondary shadow border-0">
            <CardBody className="px-lg-5 py-lg-5">
              <Form onSubmit={handleSubmit(handleEditSubmit)}>
                <h5 className="mb-3">
                  Edit Teacher: {modalData.first_name} {modalData.last_name}
                </h5>
                <Row>
                  <Col md="6">
                    <Label className="form-control-label">First Name</Label>
                    <Controller
                      name="newFirstName"
                      control={control}
                      defaultValue={modalData.first_name}
                      render={({ field }) => <Input {...field} />}
                    />
                  </Col>
                  <Col md="6">
                    <Label className="form-control-label">Last Name</Label>
                    <Controller
                      name="newLastName"
                      control={control}
                      defaultValue={modalData.last_name}
                      render={({ field }) => <Input {...field} />}
                    />
                  </Col>
                </Row>
                <Row className="mt-3">
                  <Col md="6">
                    <Label className="form-control-label">Phone</Label>
                    <Controller
                      name="newPhoneNumber"
                      control={control}
                      defaultValue={modalData.phone_number}
                      render={({ field }) => <Input {...field} />}
                    />
                  </Col>
                  <Col md="6">
                    <Label className="form-control-label">Email</Label>
                    <Controller
                      name="newEmail"
                      control={control}
                      defaultValue={modalData.email}
                      render={({ field }) => <Input {...field} />}
                    />
                  </Col>
                </Row>

                <div className="modal-footer mt-4">
                  <Button color="secondary" type="button" onClick={() => setModalOpen(false)}>
                    Close
                  </Button>
                  <Button color="primary" type="submit">
                    Save
                  </Button>
                </div>
              </Form>
            </CardBody>
          </Card>
        </div>
      </Modal>
    </>
  );
}
